import { provide } from "vue";
import {
  routeLocationKey,
  routerKey,
  useRoute,
  useRouter,
  type RouteLocationNormalizedLoaded,
  type RouteLocationRaw,
  type Router,
} from "vue-router";

/**
 * The shared document components build their entity murn from route.params.id.
 * On a dust permit version that id is the permit, so the list queries every version.
 * Child document components see id as "{permitId}/version/{versionId}". Navigation
 * still uses the numeric permit id.
 */
export function providePermitVersionDocumentContext() {
  const route = useRoute();
  const router = useRouter();

  const versionResourceId = () => `${String(route.params.id)}/version/${String(route.params.versionId)}`;

  const params = new Proxy(route.params, {
    get(target, prop, receiver) {
      if (prop === "id") return versionResourceId();
      return Reflect.get(target, prop, receiver);
    },
    ownKeys(target) {
      return Reflect.ownKeys(target);
    },
    getOwnPropertyDescriptor(target, prop) {
      if (prop === "id") {
        return { configurable: true, enumerable: true, writable: false, value: versionResourceId() };
      }
      return Reflect.getOwnPropertyDescriptor(target, prop);
    },
  });

  const documentRoute = new Proxy(route, {
    get(target, prop, receiver) {
      if (prop === "params") return params;
      return Reflect.get(target, prop, receiver);
    },
  }) as RouteLocationNormalizedLoaded;

  const documentRouter = new Proxy(router, {
    get(target, prop, receiver) {
      if (prop === "resolve" || prop === "push" || prop === "replace") {
        const method = target[prop] as (to: RouteLocationRaw, ...args: unknown[]) => unknown;
        return (to: RouteLocationRaw, ...args: unknown[]) => {
          const restored = restorePermitId(to, route);
          if (prop !== "resolve") {
            const resolved = target.resolve(restored);
            const current = target.currentRoute.value;
            if (resolved.fullPath === current.fullPath) return Promise.resolve(current);
          }
          return method.apply(target, [restored, ...args]);
        };
      }
      return Reflect.get(target, prop, receiver);
    },
  }) as Router;

  provide(routeLocationKey, documentRoute);
  provide(routerKey, documentRouter);
}

function restorePermitId(
  to: RouteLocationRaw,
  currentRoute?: { params: { id?: unknown; versionId?: unknown } },
): RouteLocationRaw {
  if (typeof to !== "object" || to == null || !("params" in to) || to.params == null) return to;
  const id = to.params.id;
  if (typeof id !== "string") return to;
  const splitAt = id.indexOf("/version/");
  let permitId = splitAt < 0 ? id : id.slice(0, splitAt);
  const versionId = String(to.params.versionId ?? currentRoute?.params.versionId ?? "");
  const currentPermitId = String(currentRoute?.params.id ?? "");
  const routeName = "name" in to ? String(to.name ?? "") : "";
  if (
    routeName.startsWith("DustPermit.DustPermitDocuments") &&
    permitId === versionId &&
    currentPermitId &&
    currentPermitId !== permitId
  ) {
    permitId = currentPermitId;
  }
  if (permitId === id) return to;
  return { ...to, params: { ...to.params, id: permitId } };
}
